import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, LogIn, PackageOpen, Mail, RefreshCw, ShieldAlert, Loader2 } from "lucide-react";
import { MaskedLines, MonoLabel } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const Field = ({ label, value }) => (
  <div>
    <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-navy/40">{label}</p>
    <p className="text-sm font-semibold text-navy mt-0.5 break-words">{value || "—"}</p>
  </div>
);

const Admin = () => {
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        setAuth(true);
      })
      .catch(() => setAuth(false));
  }, [location.state]);

  const load = async () => {
    setLoading(true);
    try {
      const [q, m] = await Promise.all([
        axios.get(`${API}/quotes`, { withCredentials: true }),
        axios.get(`${API}/messages`, { withCredentials: true }),
      ]);
      setQuotes(q.data);
      setMessages(m.data);
    } catch {
      toast.error("Could not load the inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth && user?.is_admin) load();
  }, [auth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setAuth(false);
    setUser(null);
  };

  return (
    <main className="min-h-screen bg-[#F5F8FD]">
      <section className="bg-navy-deep noise-dark pt-40 pb-16">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 relative z-10">
          <MonoLabel light className="mb-6">Owner access</MonoLabel>
          <MaskedLines
            lines={["QUOTES INBOX"]}
            className="font-display font-black uppercase tracking-tighter leading-[0.92] text-white text-[11vw] sm:text-7xl"
          />
        </div>
      </section>

      <section className="py-16" data-testid="admin-content">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          {auth === null && (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-royal" size={32} data-testid="admin-loading" />
            </div>
          )}

          {auth === false && (
            <div className="max-w-md mx-auto bg-white border border-blue-900/10 p-10 text-center shadow-[8px_8px_0px_rgba(0,51,160,0.08)]">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-royal-bright">Restricted area</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold text-navy">Sign in to continue</h2>
              <p className="mt-3 text-sm text-navy/55 font-medium leading-relaxed">
                Quote requests and contact messages contain customer details. Sign
                in with your Google account — the first account to sign in
                becomes the site admin.
              </p>
              <button
                onClick={login}
                data-testid="admin-login-button"
                className="mt-8 w-full flex items-center justify-center gap-3 h-14 bg-royal text-white font-display text-lg font-bold tracking-wide shadow-[5px_5px_0px_#040B16] hover:shadow-[2px_2px_0px_#040B16] hover:translate-x-[3px] hover:translate-y-[3px] transition-[box-shadow,transform] duration-150"
              >
                <LogIn size={18} /> CONTINUE WITH GOOGLE
              </button>
            </div>
          )}

          {auth === true && user && !user.is_admin && (
            <div className="max-w-md mx-auto bg-white border border-blue-900/10 p-10 text-center">
              <ShieldAlert size={32} className="mx-auto text-royal" />
              <h2 className="mt-4 font-display text-2xl font-extrabold text-navy">No admin access</h2>
              <p className="mt-2 text-sm text-navy/55 font-medium">
                {user.email} is signed in but is not registered as an admin.
              </p>
              <button
                onClick={logout}
                data-testid="admin-logout-button-denied"
                className="mt-6 font-mono text-sm font-bold text-royal hover:text-royal-bright transition-colors duration-200"
              >
                Sign out
              </button>
            </div>
          )}

          {auth === true && user?.is_admin && (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setTab("quotes")}
                    data-testid="admin-tab-quotes"
                    className={`flex items-center gap-2 px-5 h-11 font-mono text-sm font-bold transition-colors duration-200 ${
                      tab === "quotes" ? "bg-royal text-white" : "bg-white text-navy border border-blue-900/15 hover:border-royal"
                    }`}
                  >
                    <PackageOpen size={15} /> QUOTES ({quotes.length})
                  </button>
                  <button
                    onClick={() => setTab("messages")}
                    data-testid="admin-tab-messages"
                    className={`flex items-center gap-2 px-5 h-11 font-mono text-sm font-bold transition-colors duration-200 ${
                      tab === "messages" ? "bg-royal text-white" : "bg-white text-navy border border-blue-900/15 hover:border-royal"
                    }`}
                  >
                    <Mail size={15} /> MESSAGES ({messages.length})
                  </button>
                  <button
                    onClick={load}
                    data-testid="admin-refresh-button"
                    className="flex items-center justify-center h-11 w-11 bg-white border border-blue-900/15 text-navy hover:border-royal transition-colors duration-200"
                    aria-label="Refresh"
                  >
                    <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-mono text-xs text-navy/50">{user.email}</p>
                  <button
                    onClick={logout}
                    data-testid="admin-logout-button"
                    className="flex items-center gap-2 font-mono text-xs font-bold text-royal hover:text-royal-bright transition-colors duration-200"
                  >
                    <LogOut size={14} /> SIGN OUT
                  </button>
                </div>
              </div>

              {tab === "quotes" && (
                <div className="space-y-4" data-testid="admin-quotes-list">
                  {quotes.length === 0 && !loading && (
                    <p className="py-16 text-center font-mono text-sm text-navy/40">No quote requests yet.</p>
                  )}
                  {quotes.map((q) => (
                    <article key={q.id} data-testid={`admin-quote-${q.id}`} className="bg-white border border-blue-900/10 p-6 sm:p-8">
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
                        <h3 className="font-display text-xl font-bold text-navy">
                          {q.name} <span className="text-royal font-mono text-sm ml-2">{q.service}</span>
                        </h3>
                        <p className="font-mono text-xs text-navy/40">{fmtDate(q.created_at)}</p>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                        <Field label="Email" value={q.email} />
                        <Field label="Phone" value={q.phone} />
                        <Field label="Origin" value={q.origin} />
                        <Field label="Destination" value={q.destination} />
                        <Field label="Company" value={q.company} />
                        <Field label="Weight / pallets" value={q.weight} />
                        <div className="col-span-2">
                          <Field label="Notes" value={q.notes} />
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {tab === "messages" && (
                <div className="space-y-4" data-testid="admin-messages-list">
                  {messages.length === 0 && !loading && (
                    <p className="py-16 text-center font-mono text-sm text-navy/40">No messages yet.</p>
                  )}
                  {messages.map((m) => (
                    <article key={m.id} data-testid={`admin-message-${m.id}`} className="bg-white border border-blue-900/10 p-6 sm:p-8">
                      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
                        <h3 className="font-display text-xl font-bold text-navy">{m.name}</h3>
                        <p className="font-mono text-xs text-navy/40">{fmtDate(m.created_at)}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-5 mb-4">
                        <Field label="Email" value={m.email} />
                        <Field label="Phone" value={m.phone} />
                      </div>
                      <Field label="Message" value={m.message} />
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default Admin;
